<template>
    <div class="card">
        <div class="my-2 flex justify-content-between">
            <h6>Search Criteria:</h6>
            <Button label="Add New User" icon="pi pi-plus" class="mb-2" @click="showDialog" />
        </div>
        <Accordion @update:activeIndex="onAccordionExpand" :activeIndex="0">
            <AccordionTab header="Use as few or as many as you wish, partial matches are acceptable.">
                <!-- All the criterias here -->
                <div class="p-fluid formgrid grid">
                    <div class="field col-6 md:col-3">
                        <label for="reference">Reference:</label>
                        <InputText maxlength = "255" v-model="state.criteria.reference" placeholder="Input user reference.." v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="name">Name:</label>
                        <InputText maxlength = "255" v-model="state.criteria.name" placeholder="Input user name.."  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="surname">Surname:</label>
                        <InputText maxlength = "255" v-model="state.criteria.surname" placeholder="Input user surname.."  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="email">Email:</label>
                        <InputText maxlength = "255" v-model="state.criteria.email" placeholder="Input user name.."  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="startAge">Start Age</label>
                        <InputNumber id="startAge" v-model="state.criteria.age_start" placeholder="Start age in years.." :min="0" :max="99" :useGrouping="false" suffix=" yrs"  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="endAge">End Age</label>
                        <InputNumber id="endAge" v-model="state.criteria.age_end" placeholder="End age in years.." :min="0" :max="99" :useGrouping="false" suffix=" yrs"  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="startDOB">Starting Date of Birth</label>
                        <Calendar :showIcon="true" inputId="startDate" v-model="state.criteria.dob_start" dateFormat="dd-mm-yy"   v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="endDOB">Ending Date of Birth</label>
                        <Calendar :showIcon="true" inputId="endDate" v-model="state.criteria.dob_end" dateFormat="dd-mm-yy"  v-on:keyup.enter="loadCriteria" />
                    </div>

                    <div class="field col-6 md:col-3">
                        <label for="sex">Sex:</label>
                        <Dropdown v-model="state.criteria.sex" :options="state.sexes" optionLabel="name" optionValue="id" placeholder="Choose Sex" :showClear="true"  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <!-- Dynamic User Profile attribute filters -->
                    <div v-for="attribute in filteredProfileAttributes" class="field col-6 md:col-3" :key="attribute.id">
                        <label :for="attribute.label">{{ attribute.label }}:</label>
                        <Dropdown v-model="dynamicCriteria[attribute.code]" :options="attribute.options" optionLabel="label" optionValue="value" :placeholder="'Select ' + attribute.label" :showClear="true"  v-on:keyup.enter="loadCriteria"/>
                    </div>
                    <div class="field col-6 md:col-3">
                        <label for="user_status">Archive Filter:</label>
                        <Dropdown v-model="state.criteria.user_status" :options="state.statuses" optionLabel="name" optionValue="id" placeholder="Choose Status" :showClear="true"  v-on:keyup.enter="loadCriteria"/>
                    </div>

                </div>
                <Button type="button" label="Search" icon="pi pi-search" :loading="state.loading" @click="loadCriteria()" />
            </AccordionTab>
        </Accordion>


        <div class="my-2 flex justify-content-end" v-if="displayMultipleCreateButtons">
            <!--
            Yes, the button is displayed on top of filters.
            If I miss it looking to the list, also users probably will.
            For aesthetical reasons, it's hided when the accordion is collapsed.
            -->
            <Button label="Add New User" icon="pi pi-plus" class="mb-2" @click="showDialog" />
        </div>
    </div>
    <add-user v-if="state.userDialog" @hide-dialog="hideDialog" />
</template>

<script setup>
import { ref, computed, onMounted, reactive, defineEmits, onUnmounted } from 'vue';
import AddUser from '../user/AddUser.vue';
import { useAttributeStore } from '../../products/pha/modules/attribute/store';
import { getAllSexes, getUserStatuses } from '../user-groups/enum-helpers';
import { UserProfileTypes } from '../../products/pha/modules/attribute/enum';
import { storeToRefs } from 'pinia';
import store from '@/store';

const attributeStore = useAttributeStore();
const { params, allAttributes } = storeToRefs(attributeStore);
// this variable will store all the user profile attributes in the begining
// const profileAttributes = ref([]);
const dynamicCriteria = ref([]);
const attributes = ref([]);
const values = ref([]);

const isCstOnly = store.getters['auth/isCstOnly'];

const emit = defineEmits(['set-criteria']);

const displayMultipleCreateButtons = ref(true)

onMounted(async () => {
    params.value.user_profile = UserProfileTypes.USER_PROFILE;

    if (!isCstOnly) {
        await attributeStore.fetchAllAttributes();
    }
    // profileAttributes.value = await attributeStore.fetchAllAttributes('Company', userProfileCriteria.value);
});

onUnmounted(() => {
    attributeStore.clearFilters();
});

function onAccordionExpand(index) {
    displayMultipleCreateButtons.value = (index !== null)
}

// it will filter out all the unnecessary attributes
const filteredProfileAttributes = computed(() => {
    // filter out unnecessary attributes which already exists or is not required
    const filterList = ['user_dob', 'user_company_type', 'user_name', 'user_surname', 'user_reference', 'user_sex', 'user_dob'];
    return allAttributes.value?.filter((attribute) => {
        // First make the filtered list
        const returnList = !filterList.includes(attribute.code) && attribute.type == 8;
        // if attribute code is new then push it to filterList to avoid same attribute code extraction in next step
        if (!filterList.includes(attribute.code)) {
            filterList.push(attribute.code);
        }
        return returnList;
    });
});

const createInitialState = () => {
    return reactive({
        criteria: {
            name: null,
            surname: null,
            sex: null,
            email: null,
            reference: null,
            age_start: null,
            age_end: null,
            dob_start: null,
            dob_end: null,
            user_status: 2, // Default to "Exclude archived (Active only)"
        },
        sexes: getAllSexes(),
        statuses: getUserStatuses(),
        userDialog: false,
        loading: false,
    });
};

const state = createInitialState();

const loadCriteria = () => {
    state.loading = true;
    // clean off before setting new values
    attributes.value = [];
    values.value = [];
    // extract the dynamicCriteria details and fill up the params for attributes and values
    if (dynamicCriteria.value && dynamicCriteria.value !== {}) {
        state.criteria.attribute = [];
        state.criteria.value = [];
        Object.keys(dynamicCriteria.value).forEach((key) => {
            if (dynamicCriteria.value[key]) {
                attributes.value.push(key);
                values.value.push(dynamicCriteria.value[key]);
            }
        });
        if (attributes.value.length && values.value.length) {
            state.criteria.attribute = attributes.value;
            state.criteria.value = values.value;
        }
    }
    emit('set-criteria', state.criteria);
    setTimeout(() => {
        state.loading = false;
    }, 2000);
};

const showDialog = () => {
    state.userDialog = true;
};
const hideDialog = () => {
    state.userDialog = false;
};
</script>
